<template>
  <div class="microblocks-sequence">
    <TransitionGroup
      ref="microblocksSequence"
      class="microblocks-sequence__sequence"
      name="microblocks-sequence"
      tag="div">
      <div
        v-for="microblock in microblocks"
        :key="microblock.hash + microblock.prevKeyHash"
        :class="[
          'microblocks-sequence__cell',
          {'microblocks-sequence__cell--active': microblock.hash === selectedMicroblock.hash}]"
        @click="selectMicroblock(microblock)">
        {{ microblock.transactionsCount }}
      </div>
    </TransitionGroup>
    <div class="microblocks-sequence__overlay"/>
  </div>
</template>

<script setup>
const { selectMicroblock } = useRecentBlocksStore()
const { selectedMicroblock } = storeToRefs(useRecentBlocksStore())

const props = defineProps({
  microblocks: {
    required: true,
    type: Array,
  },
})

const microblocksSequence = ref(null)

watch(
  () => props.microblocks?.length,
  async (newLen, oldLen) => {
    if (!oldLen || newLen <= oldLen) return
    await nextTick()
    const el = microblocksSequence.value.$el
    if (el.scrollLeft > 0) {
      const firstCell = el.querySelector('.microblocks-sequence__cell')
      if (firstCell) {
        const marginRight = parseFloat(window.getComputedStyle(firstCell).marginRight) || 0
        el.scrollLeft += firstCell.offsetWidth + marginRight
      }
    }
  })

watch(
  selectedMicroblock,
  async (newBlock, oldBlock) => {
    const el = microblocksSequence.value.$el
    if (newBlock?.height !== oldBlock?.height) {
      el.scrollLeft = 0
    } else {
      await nextTick()
      const activeCell = el.querySelector('.microblocks-sequence__cell--active')
      if (activeCell) {
        const targetScroll = activeCell.offsetLeft - (el.offsetWidth / 2) + (activeCell.offsetWidth / 2)
        el.scrollTo({ left: targetScroll, behavior: 'smooth' })
      }
    }
  })
</script>

<style scoped>
.microblocks-sequence {
  position: relative;

  &__sequence {
    display: flex;
    height: 24px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__cell {
    display: flex;
    align-items: center;
    justify-content: center;

    min-width: 40px;
    height: 100%;

    border-radius: 4px;
    margin-right: var(--space-1);
    background: var(--color-midnight-35);

    color: var(--color-white);
    font-family: var(--font-monospaced);
    font-size: 14px;
    cursor: pointer;

    &--active {
      background: var(--color-fire);
    }
  }

  &__overlay {
    height: 100%;
    width: 80px;
    position: absolute;
    right: 0;
    top: 0;
    pointer-events: none;
    background-image: linear-gradient(
      90deg,
      rgb(var(--color-surface-rgb) / 0%) 0,
      rgb(var(--color-surface-rgb) / 100%) 100%
    );

    @media (--desktop) {
      width: 200px;
    }
  }

  &-move,
  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: scale(0);
  }
}
</style>
